import { useState } from 'react';
import { Card, CardBody } from '@components/ui';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  KeyIcon,
  DocumentTextIcon,
  BuildingLibraryIcon,
  ComputerDesktopIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@lib/utils/cn';

interface FAQItem {
  question: string;
  answer: string | string[];
}

interface FAQCategory {
  id: string;
  title: string;
  icon: React.ReactNode;
  items: FAQItem[];
}

const faqData: FAQCategory[] = [
  {
    id: 'firma-electronica',
    title: 'Firma Electrónica',
    icon: <KeyIcon className="h-6 w-6" />,
    items: [
      {
        question: '¿Cuánto tiempo toma el trámite de la firma electrónica cuando es gestionado por ustedes?',
        answer: 'El trámite de la firma electrónica, cuando es gestionado con nuestro acompañamiento, suele completarse en un plazo aproximado de 3 a 7 días hábiles, dependiendo de la validación de la documentación y de la disponibilidad del contribuyente para completar el proceso de emisión de apoderado, declaración jurada y afiliación al PAC.',
      },
      {
        question: '¿Qué requisitos y documentación se necesitan para iniciar el trámite de la firma electrónica?',
        answer: [
          'Cédula o pasaporte vigente del contribuyente o representante legal.',
          'Aviso de operaciones, RUC actualizado.',
          'En caso de personas jurídicas, documentación que acredite la representación legal. En caso de persona natural, se debe solicitar la afiliación como contribuyente.',
          'Documento de apoderado emitido desde el sistema Etax del cliente.',
        ],
      },
      {
        question: '¿Cuál es el costo, vigencia y periodicidad de renovación de la firma electrónica?',
        answer: 'La firma electrónica tiene un costo único de emisión con nosotros de $119.99, una vigencia de 2 años, y debe renovarse al finalizar dicho período. La renovación implica un nuevo pago y validación, ya que la firma tiene un vencimiento por normativa de seguridad.',
      },
      {
        question: '¿La firma electrónica queda a nombre del contribuyente?',
        answer: 'Sí. La firma electrónica queda siempre a nombre del contribuyente o representante legal, no del proveedor del sistema de facturación. Es un activo digital personal e intransferible.',
      },
      {
        question: '¿Puede utilizarse la firma electrónica con otro proveedor?',
        answer: 'Sí. La firma electrónica es independiente del sistema de facturación y puede utilizarse con cualquier proveedor que cumpla con los estándares y normativas de la DGI.',
      },
      {
        question: '¿Todos los facturadores necesitan contar con firma electrónica?',
        answer: 'Sí. Todo contribuyente que emita facturación electrónica debe contar con una firma electrónica válida, ya que esta es necesaria para firmar digitalmente los documentos fiscales y garantizar su autenticidad e integridad.',
      },
    ],
  },
  {
    id: 'documento-apoderado',
    title: 'Documento de Apoderado',
    icon: <DocumentTextIcon className="h-6 w-6" />,
    items: [
      {
        question: '¿Qué es el documento de apoderado y para qué sirve?',
        answer: 'El documento de apoderado es una autorización formal mediante la cual el contribuyente permite que un tercero (por ejemplo, un proveedor tecnológico) realice trámites ante la DGI en su nombre, como la habilitación del sistema de facturación electrónica.',
      },
      {
        question: '¿Cómo se descarga el documento de apoderado?',
        answer: 'El documento se descarga directamente desde el portal de la DGI (sistema Etax), dentro del rubro de Factura Electrónica. Allí se desplegará la opción de "Apoderado de Retiro de Firma Electrónica (FEP)".',
      },
      {
        question: '¿Ustedes brindan acompañamiento para completar o validar el documento de apoderado?',
        answer: 'Sí. Brindamos acompañamiento por medio del chat de soporte y una guía en formato PDF del paso a paso para completar correctamente el documento, asegurando que cumpla con los requisitos exigidos por la DGI y evitando rechazos o reprocesos.',
      },
    ],
  },
  {
    id: 'facturacion-dgi',
    title: 'Facturación Electrónica y DGI',
    icon: <BuildingLibraryIcon className="h-6 w-6" />,
    items: [
      {
        question: 'Si el contribuyente utilizaba el facturador gratuito, ¿el tipo de presentación debe ser Original o Rectificativa?',
        answer: 'Debe ser rectificativa ya que la original se realizó al momento de integrarse al Facturador Gratuito de la DGI.',
      },
      {
        question: '¿Pueden proporcionar la resolución de la DGI correspondiente al sistema de facturación?',
        answer: 'Sí. Se puede proporcionar la resolución oficial de la DGI que avala el sistema de facturación electrónica utilizado, como respaldo normativo para el contribuyente.',
      },
      {
        question: '¿Desde el portal se pueden visualizar las facturas recibidas?',
        answer: 'Sí. El sistema permite visualizar tanto facturas emitidas como facturas recibidas, además de un balance general, facilitando el control administrativo y contable del contribuyente.',
      },
    ],
  },
  {
    id: 'uso-sistema',
    title: 'Uso del Sistema de Facturación',
    icon: <ComputerDesktopIcon className="h-6 w-6" />,
    items: [
      {
        question: '¿Los folios de facturación son ilimitados?',
        answer: 'Depende del plan contratado. Algunos planes tienen límites mensuales como el Plan Free, mientras que otros ofrecen folios ilimitados y funcionalidades adicionales.',
      },
      {
        question: '¿Las facturas emitidas incluyen el logo de la empresa?',
        answer: 'Sí. El sistema permite personalizar las facturas, incluyendo el logo de la empresa y otros datos visuales dependiendo del plan.',
      },
      {
        question: '¿Es posible generar ofertas o cotizaciones y posteriormente convertirlas en facturas?',
        answer: 'No. El sistema actualmente no permite la creación de cotizaciones, únicamente la emisión de facturación electrónica.',
      },
      {
        question: '¿Se puede modificar la numeración inicial de las facturas para dar continuidad a facturas emitidas previamente?',
        answer: 'Sí. Esta opción solo aplica a facturas de crédito.',
      },
      {
        question: '¿Las facturas generadas desde el portal son compatibles con impresoras térmicas de 80 mm?',
        answer: 'No. Solo se puede imprimir en formato tamaño carta y remitir directamente la factura por medio de correo electrónico.',
      },
    ],
  },
  {
    id: 'planes-funcionalidades',
    title: 'Planes y Funcionalidades',
    icon: <CurrencyDollarIcon className="h-6 w-6" />,
    items: [
      {
        question: '¿Cuál es el costo del sistema de facturación electrónica?',
        answer: 'El costo varía según el plan seleccionado y las funcionalidades incluidas. Existen el Plan Free que es totalmente gratuito, el Plan Emprende que comprende un total de 750 folios anuales, el Plan Ilimitado y Premium que consta de folios ilimitados, y todos se encuentran diseñados para distintos volúmenes de facturación.',
      },
      {
        question: '¿Qué incluye cada uno de los planes disponibles?',
        answer: [
          'Cantidad de documentos emitidos.',
          'Usuarios.',
          'Reportes.',
          'Soporte técnico.',
          'Personalización.',
        ],
      },
      {
        question: '¿El plan ilimitado incluye módulo de inventario?',
        answer: 'No. La plataforma no maneja módulos adicionales ni integraciones a otras plataformas por el momento.',
      },
      {
        question: '¿El sistema permite configurar fechas de vencimiento en las facturas?',
        answer: '',
      },
    ],
  },
  {
    id: 'demo-soporte',
    title: 'Demo y Soporte',
    icon: <ChatBubbleLeftRightIcon className="h-6 w-6" />,
    items: [
      {
        question: '¿El sistema cuenta con un demo para evaluación previa?',
        answer: 'Al ser un portal completamente intuitivo y auto gestionable, no se brindan demostraciones personalizadas. El cliente puede inscribirse a nuestro plan gratuito y de ese modo generar las pruebas pertinentes y la visualización del portal en tiempo real.',
      },
      {
        question: '¿Qué tipo de soporte se ofrece a los clientes activos?',
        answer: 'Se ofrece soporte técnico y funcional por medio de correo y chat, dependiendo también del tipo de plan se puede considerar un soporte prioritario.',
      },
      {
        question: '¿A través de qué canales se brinda el soporte?',
        answer: 'El soporte se brinda a través de correo electrónico, chat y otros canales digitales, según el plan contratado.',
      },
      {
        question: '¿Cómo se gestionan las consultas o incidencias una vez el cliente está utilizando el sistema?',
        answer: 'Las consultas e incidencias se gestionan mediante un flujo de atención por medio de tickets y personalizadamente por medio de llamada y chat de atención al cliente.',
      },
    ],
  },
];

function FAQAccordionItem({ item, isOpen, onToggle }: { item: FAQItem; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-secondary-200 dark:border-secondary-700 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 px-4 text-left hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors"
      >
        <span className="font-medium text-secondary-900 dark:text-white pr-4">
          {item.question}
        </span>
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-secondary-400 flex-shrink-0" />
        )}
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="px-4 pb-4 text-secondary-600 dark:text-secondary-400">
          {Array.isArray(item.answer) ? (
            <ul className="list-disc list-inside space-y-2">
              {item.answer.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </ul>
          ) : item.answer ? (
            <p>{item.answer}</p>
          ) : (
            <p className="italic text-secondary-400 dark:text-secondary-500">Respuesta pendiente de documentar.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function FAQCategorySection({ category }: { category: FAQCategory }) {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const expandAll = () => {
    setOpenItems(new Set(category.items.map((_, index) => index)));
  };

  const collapseAll = () => {
    setOpenItems(new Set());
  };

  return (
    <Card className="overflow-hidden">
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-white">{category.icon}</div>
            <h2 className="text-lg font-semibold text-white">{category.title}</h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="text-xs text-white/80 hover:text-white transition-colors"
            >
              Expandir todo
            </button>
            <span className="text-white/50">|</span>
            <button
              onClick={collapseAll}
              className="text-xs text-white/80 hover:text-white transition-colors"
            >
              Colapsar todo
            </button>
          </div>
        </div>
      </div>
      <CardBody className="p-0">
        {category.items.map((item, index) => (
          <FAQAccordionItem
            key={index}
            item={item}
            isOpen={openItems.has(index)}
            onToggle={() => toggleItem(index)}
          />
        ))}
      </CardBody>
    </Card>
  );
}

export function FAQPage() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCategories = faqData.map(category => ({
    ...category,
    items: category.items.filter(item =>
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(item.answer)
        ? item.answer.some(a => a.toLowerCase().includes(searchTerm.toLowerCase()))
        : item.answer.toLowerCase().includes(searchTerm.toLowerCase()))
    ),
  })).filter(category => category.items.length > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Preguntas Frecuentes
          </h1>
          <p className="mt-1 text-secondary-600 dark:text-secondary-400">
            Encuentra respuestas a las consultas más comunes sobre Factura Libre
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Buscar en las preguntas frecuentes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-3 pl-10 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* FAQ Categories */}
      {filteredCategories.length > 0 ? (
        <div className="space-y-6">
          {filteredCategories.map((category) => (
            <FAQCategorySection key={category.id} category={category} />
          ))}
        </div>
      ) : (
        <Card>
          <CardBody className="text-center py-12">
            <p className="text-secondary-500 dark:text-secondary-400">
              No se encontraron resultados para "{searchTerm}"
            </p>
          </CardBody>
        </Card>
      )}

    </div>
  );
}
